import { FC, useEffect, useState } from "react";
import TrendingUser from "./component/TrendingUser";
import { useGetTrendingStocksQuery } from "../../../store/api/trendingStocksApi";
import CustomToast from "@/components/UI/CustomToast/CustomToast";
import { useAppSelector } from "@/hooks/useAppSelector";
import { useGetTrendingIndustriesQuery, useGetTrendingSectorsQuery } from "@/store/api/financeApi";
import { TimeRangeValue } from "@/types/timeRanges";
import Trending from "./component/Trending";
import SearchCompanies from "../SearchCompanies/SearchCompanies";

export const TRENDING_ITEMS_LIMIT = 7;

const RightSidebar: FC = () => {
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [filterTimeTrendingUsers, setFilterTimeTrendingUsers] = useState("1 day");
  const [filterTime, setFilterTime] = useState<TimeRangeValue>("24h");
  const [subtype, setSubtype] = useState<string[]>([]);
  const [activeSortTab, setActiveSortTab] = useState<"post" | "likes" | "liked" | "sentiment">(
    "post"
  );
  const [toast, setToast] = useState<{ message: string; success?: boolean } | null>(null);

  const { subscriptionStatus, subscriptionLoading } = useAppSelector((state) => state.auth);
  const hasPremium = subscriptionStatus === "ACTIVE";

  type TabType = "STOCK" | "SECTOR" | "INDUSTRY" | "ETF" | "INDEX" | "ALL";
  const { activeTab, view } = useAppSelector((state) => state.rightSidebar) as {
    activeTab: TabType;
    view: "all" | "follows";
  };
  useEffect(() => {
    if (view === "all" && activeSortTab === "liked") {
      setActiveSortTab("post");
    }
  }, [view, setActiveSortTab]);
  const effectiveSortOrder = activeSortTab === "sentiment" ? sortOrder : "desc";
  const [displayData, setDisplayData] = useState<
    {
      name: string;
      posts: number;
      likes: number;
      sentiment: number;
      symbol: string;
      industry?: string;
      sector?: string;
    }[]
  >([]);

  const tabs: { label: string; value: TabType }[] = [
    { label: "Stocks", value: "STOCK" },
    { label: "Sectors", value: "SECTOR" },
    { label: "Industry", value: "INDUSTRY" },
    // { label: "Indexes", value: "INDEX" },
    { label: "ETF", value: "ETF" },
  ];

  const hideAsc = sortOrder === "asc";
  const hideDesc = sortOrder === "desc";

  const sortVariantMap = {
    post: "Posts",
    likes: "Likes",
    liked: "Liked",
    sentiment: "Sentiment",
  } as const;

  const handleSort = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const isStocksTab = activeTab === "STOCK" || activeTab === "ETF";
  const isSectorTab = activeTab === "SECTOR";
  const isIndustryTab = activeTab === "INDUSTRY";

  // STOCKS + ETF
  const trendingStocks = useGetTrendingStocksQuery(
    {
      type: activeTab === "ETF" ? "ETF" : "STOCK",
      filter: view === "all" ? "all" : "following",
      page: 1,
      limit: TRENDING_ITEMS_LIMIT,
      time_range: filterTime || undefined,
      sortBy: activeSortTab, // posts | likes | sentiment
      order: effectiveSortOrder,
      ...(subtype.length > 0 && !subtype.includes("All") ? { subtype } : {}),
    },
    {
      skip: !isStocksTab,
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
      refetchOnFocus: true,
    }
  );

  // SECTORS
  const trendingSectors = useGetTrendingSectorsQuery(
    {
      page: 1,
      limit: TRENDING_ITEMS_LIMIT,
      filter: view === "all" ? "all" : "following",
      time_range: filterTime || undefined,

      sortBy: activeSortTab,
      order: effectiveSortOrder,
      ...(subtype.length > 0 && !subtype.includes("All") ? { subtype } : {}),
    },
    {
      skip: !isSectorTab,
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
      refetchOnFocus: true,
    }
  );

  // INDUSTRIES
  const trendingIndustries = useGetTrendingIndustriesQuery(
    {
      page: 1,
      limit: TRENDING_ITEMS_LIMIT,
      filter: view === "all" ? "all" : "following",
      time_range: filterTime || undefined,

      sortBy: activeSortTab,
      order: effectiveSortOrder,
      ...(subtype.length > 0 && !subtype.includes("All") ? { subtype } : {}),
    },
    {
      skip: !isIndustryTab,
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
      refetchOnFocus: true,
    }
  );

  let apiData, isError, isFetching;

  if (isSectorTab) {
    apiData = trendingSectors.data;
    isError = trendingSectors.isError;
    isFetching = trendingSectors.isFetching;
  } else if (isIndustryTab) {
    apiData = trendingIndustries.data;
    isError = trendingIndustries.isError;
    isFetching = trendingIndustries.isFetching;
  } else {
    apiData = trendingStocks.data;
    isError = trendingStocks.isError;
    isFetching = trendingStocks.isFetching;
  }

  useEffect(() => {
    if (!isError && apiData && Array.isArray(apiData.data)) {
      const mapped = apiData.data.map((item: any) => {
        const sentiment = item.sentiment ?? 5;
        if (activeTab === "SECTOR" || activeTab === "INDUSTRY") {
          return {
            name: item.name,
            symbol: item.symbol ?? "",
            posts: item.posts ?? 0,
            likes: item.likes ?? 0,
            liked: item.likedByFollowsCount ?? 0,
            sentiment,
            industry: activeTab === "INDUSTRY" ? item.name : (item.industry ?? ""),
            sector: activeTab === "SECTOR" ? item.name : (item.sector ?? ""),
          };
        } else {
          return {
            name: item.name,
            symbol: item.symbol,
            posts: item.mentionCount ?? 0,
            likes: item.likeCount ?? 0,
            liked: item.liked ?? 0,
            sentiment,
            industry: item.industry ?? "",
            sector: item.sector ?? "",
          };
        }
      });
      setDisplayData(mapped);
    } else {
      setDisplayData([]);
    }
  }, [apiData, isError, activeTab]);

  return (
    <div
      style={{
        height: "100%",
        marginLeft: "24px",
        overflowY: "auto",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        background: "#fff",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        // overflow: "visible",
      }}
    >
      {toast && (
        <CustomToast
          message={toast.message}
          success={toast.success}
          onClose={() => setToast(null)}
        />
      )}
      <SearchCompanies />
      <Trending
        hasPremium={hasPremium}
        subscriptionLoading={subscriptionLoading}
        toast={toast}
        setToast={setToast}
        tabs={tabs}
        filterTime={filterTime}
        setFilterTime={setFilterTime}
        subtype={subtype}
        setSubtype={setSubtype}
        activeSortTab={activeSortTab}
        setActiveSortTab={setActiveSortTab}
        sortOrder={sortOrder}
        handleSort={handleSort}
        hideAsc={hideAsc}
        hideDesc={hideDesc}
        sortVariantMap={sortVariantMap}
        isFetching={isFetching}
        sortedData={displayData}
        view={view}
        activeTab={activeTab}
      />

      <TrendingUser
        filterTimeTrendingUsers={filterTimeTrendingUsers}
        setFilterTimeTrendingUsers={setFilterTimeTrendingUsers}
      />
    </div>
  );
};

export default RightSidebar;
